import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft, Facebook, Twitter, Linkedin, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import BlogSidebar from "@/components/BlogSidebar";

export default function BlogPostChiroThinDiet() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Understanding the ChiroThin Dietary Guidelines | Longview, TX"
        description="Curious about what you actually eat on the ChiroThin program? Learn how real, anti-inflammatory foods drive sustainable weight loss in East Texas."
        ogImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/731c23db-1b39-4bf4-a604-5dd17c931766.png&w=1280&output=webp&q=80"
        canonical="https://woodsspine.com/blog/chirothin-dietary-guidelines"
      />
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-12">
        <Button variant="ghost" className="mb-6 hover:bg-transparent pl-0 text-muted-foreground hover:text-foreground" asChild>
          <Link to="/blog">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
        </Button>
        <div className="lg:grid lg:grid-cols-[1fr_350px] gap-12">
          <div className="space-y-8">
            <header className="space-y-4">
              <Badge className="bg-accent text-white hover:bg-accent/90 border-0 shadow-sm">
                ChiroThin Weight Loss
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight text-primary">
                Understanding the ChiroThin Dietary Guidelines: Real Food, Real Results
              </h1>
              <div className="flex flex-wrap items-center text-sm text-muted-foreground gap-4">
                <Link to="/about#dr-lance-potter" className="flex items-center hover:text-accent transition-colors">
                  <User className="w-4 h-4 mr-2" />
                  Dr. Lance Potter
                </Link>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  July 10, 2026
                </span>
                <span>• 5 min read</span>
              </div>
            </header>

            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/731c23db-1b39-4bf4-a604-5dd17c931766.png&w=1280&output=webp&q=80" 
              alt="Person preparing a healthy portion-controlled meal following ChiroThin guidelines in Longview TX" 
              className="w-full rounded-2xl aspect-video object-cover"
              fetchPriority="high"
              decoding="async"
            />

            <article className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-xl font-medium text-foreground">
                One of the most common questions patients ask before starting a weight loss journey is, "What am I actually allowed to eat?" If you are tired of choking down chalky shakes or buying expensive, pre-packaged meals, the ChiroThin program offers a refreshing alternative. Let's break down the dietary guidelines that make this program so successful.
              </p>
              <p>
                The <Link to="/longview-chirothin-weight-loss" className="text-accent hover:underline">ChiroThin</Link> program is built on the foundation of eating real, whole foods. The goal is not just to help you lose weight rapidly during the 6-week protocol, but to teach you how to shop, cook, and eat in a way that sustains your health for the rest of your life.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">The Core Principles of the ChiroThin Diet</h2>
              <p>
                During the weight loss phase of the program, your diet will focus strictly on specific combinations of lean proteins, fresh vegetables, and low-glycemic fruits. 
              </p>
              <ul>
                <li><strong>Lean Proteins:</strong> You will eat high-quality proteins like chicken breast, lean beef, white fish, and eggs. Protein is essential for maintaining your lean muscle mass while your body burns stored fat for energy.</li>
                <li><strong>Anti-Inflammatory Vegetables:</strong> The program emphasizes vegetables that are high in fiber and nutrients but low in starch, such as spinach, asparagus, tomatoes, and cucumbers.</li>
                <li><strong>Low-Glycemic Fruits:</strong> You are allowed specific fruits, like apples and berries, which provide natural energy and antioxidants without causing massive spikes in your blood sugar.</li>
              </ul>
              <p>
                Equally important is what you <em>won't</em> be eating. The protocol strictly eliminates highly processed foods, refined sugars, artificial sweeteners, and heavy starches. These are the primary drivers of systemic inflammation and insulin resistance.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Why the Drops Matter</h2>
              <p>
                Eating a clean, low-calorie diet is difficult for most people because it usually leads to intense hunger and fatigue. This is where the proprietary ChiroThin nutritional drops come in. The drops contain a specific blend of amino acids, vitamins, and cell salts that help stabilize your blood sugar, suppress cravings, and aid in fatty acid transportation. They signal your body to tap into its stored fat reserves for the energy it needs, making the dietary guidelines comfortable and sustainable to follow.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Clinical Evidence and Credibility</h2>
              <p>
                The dietary principles behind ChiroThin are firmly rooted in nutritional science. By keeping the diet low-glycemic, the program prevents the insulin spikes that signal the body to store fat. When insulin levels remain low and stable, the body is forced to enter a lipolytic (fat-burning) state. This doctor-supervised approach ensures that patients lose fat while preserving their essential lean body mass, which is critical for maintaining a healthy basal metabolic rate long-term.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Empowering East Texas Residents</h2>
              <p>
                At Woods Spine & Wellness in Longview, TX, Dr. Lance Potter and Dr. Tucker Woods want to empower you to take control of your health. We provide you with detailed shopping lists, portion guidelines, and continuous support throughout the 6-week program. We teach you how to navigate your local grocery store so you can continue making healthy choices long after the program ends. For a comprehensive look at how we support your health, visit our <Link to="/what-we-treat" className="text-accent hover:underline">What We Treat</Link> page.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-6 mt-6">
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Can I eat out at restaurants while on the program?</h3>
                  <p className="text-muted-foreground">While cooking at home gives you the most control over your ingredients and portions, it is possible to eat out if you are careful. We will teach you how to order lean proteins and steamed vegetables without hidden oils or sugars.</p>
                </div>
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Do I have to weigh my food?</h3>
                  <p className="text-muted-foreground">Yes, during the 6-week weight loss phase, portion control is critical to the program's success. Weighing your proteins ensures you are getting exactly the right amount of calories to trigger fat burning without losing muscle.</p>
                </div>
                <div className="pb-2">
                  <h3 className="text-lg font-bold text-primary mb-2">What happens when the 6 weeks are over?</h3>
                  <p className="text-muted-foreground">The program includes a structured "maintenance phase." During this time, we guide you through slowly reintroducing healthy fats and a wider variety of foods while monitoring your weight to ensure you maintain your results.</p>
                </div>
              </div>

            </article>

            {/* Social Sharing */}
            <div className="flex items-center gap-4 pt-8 border-t border-border">
              <span className="font-medium text-muted-foreground">Share this article:</span>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <LinkIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* CTA Block */}
            <div className="bg-secondary/30 p-8 rounded-xl border border-border mt-8">
              <h3 className="text-2xl font-bold text-primary mb-3">Eat real food, get real results</h3>
              <p className="text-muted-foreground mb-6">
                Ready to learn how to fuel your body for sustainable weight loss? Schedule your ChiroThin consultation today.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">Get Started</Link>
                </Button>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="text-sm text-muted-foreground border-t border-border pt-6 mt-8">
              Disclaimer: The information provided in this article is for educational purposes only and does not constitute medical advice. Please consult with a healthcare provider before starting any new treatment.
            </div>
          </div>

          <BlogSidebar />
        </div>
      </main>
      <Footer />
    </div>
  );
}
